import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  getMe,
  getBillingInfo,
  createSubscriptionCheckout,
  createCreditsCheckout,
  createPortalSession,
} from '../api/client';
import type { UserMe, BillingInfo, SubscriptionPlan } from '../api/types';
import { ApiClientError } from '../api/types';
import { motionStyles, boxSizingStyles, responsiveStyles } from '../styles/lit-shared';

interface PlanCard {
  key: SubscriptionPlan;
  name: string;
  credits: number;
  price: number;
}

const PLANS: PlanCard[] = [
  { key: 'starter', name: 'Starter', credits: 50, price: 29 },
  { key: 'growth', name: 'Growth', credits: 150, price: 59 },
  { key: 'scale', name: 'Scale', credits: 400, price: 119 },
];

@customElement('credits-page')
export class CreditsPage extends LitElement {
  static styles = [
    boxSizingStyles,
    responsiveStyles,
    motionStyles,
    css`
    :host {
      display: block;
    }

    .hero {
      position: relative;
      margin-bottom: var(--space-2xl);
      padding: var(--space-xl);
      border-radius: var(--radius-lg);
      background: var(--gradient-primary-soft);
      border: 1px solid var(--color-border-glow);
      overflow: hidden;
    }

    .hero::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 300px;
      height: 300px;
      border-radius: 50%;
      background: var(--glow-violet);
      filter: blur(50px);
      pointer-events: none;
    }

    .hero-content {
      position: relative;
      z-index: 1;
    }

    .hero-eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      margin-bottom: var(--space-sm);
      padding: 0.25rem 0.75rem;
      border-radius: var(--radius-full);
      background: rgb(255 255 255 / 6%);
      border: 1px solid var(--color-border);
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-accent);
    }

    h1 {
      font-size: clamp(1.75rem, 4vw, 2.25rem);
      font-weight: 700;
      letter-spacing: -0.03em;
      margin-bottom: var(--space-xs);
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .subtitle {
      color: var(--color-text-muted);
      font-size: 1rem;
    }

    .section {
      margin-top: var(--space-2xl);
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      margin-bottom: var(--space-lg);
    }

    .section-header h2 {
      font-size: 1.125rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: var(--color-text);
    }

    .section-header::after {
      content: '';
      flex: 1;
      height: 1px;
      background: linear-gradient(90deg, var(--color-border-strong) 0%, transparent 100%);
    }

    .current-plan {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--space-md);
      padding: var(--space-lg);
      border-radius: var(--radius);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      margin-bottom: var(--space-lg);
    }

    .current-plan-info {
      flex: 1;
      min-width: 0;
    }

    .current-plan-label {
      font-size: 0.8125rem;
      color: var(--color-text-muted);
      margin-bottom: 0.25rem;
    }

    .current-plan-name {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--color-text);
    }

    .current-plan-note {
      margin: 0.375rem 0 0;
      font-size: 0.8125rem;
      color: var(--color-text-muted);
    }

    .current-plan-badge {
      display: inline-flex;
      align-items: center;
      padding: 0.25rem 0.625rem;
      border-radius: var(--radius-full);
      font-size: 0.6875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .badge--active {
      background: rgb(16 185 129 / 15%);
      color: var(--palette-emerald-400);
      border: 1px solid rgb(16 185 129 / 30%);
    }

    .badge--canceling {
      background: rgb(245 158 11 / 15%);
      color: rgb(251 191 36);
      border: 1px solid rgb(245 158 11 / 30%);
    }

    .badge--canceled {
      background: rgb(244 63 94 / 10%);
      color: var(--color-error);
      border: 1px solid rgb(244 63 94 / 25%);
    }

    .badge--none {
      background: rgb(255 255 255 / 5%);
      color: var(--color-text-muted);
      border: 1px solid var(--color-border);
    }

    .manage-btn {
      padding: 0.5rem 1rem;
      border-radius: var(--radius);
      background: var(--color-surface-hover);
      border: 1px solid var(--color-border);
      color: var(--color-text);
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;
      transition: background var(--duration-fast) var(--ease-out);
    }

    .manage-btn:hover {
      background: var(--color-border);
    }

    .plans-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: var(--space-lg);
    }

    .plan-card {
      position: relative;
      padding: var(--space-lg);
      border-radius: var(--radius-lg);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      transition: border-color var(--duration-fast) var(--ease-out);
    }

    .plan-card:hover {
      border-color: var(--color-border-strong);
    }

    .plan-card--current {
      border-color: var(--color-primary-light);
      box-shadow: 0 0 24px rgb(139 92 246 / 12%);
    }

    .plan-current-tag {
      position: absolute;
      top: -0.625rem;
      right: var(--space-md);
      padding: 0.25rem 0.625rem;
      border-radius: var(--radius-full);
      background: var(--gradient-primary);
      color: var(--palette-white);
      font-size: 0.6875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .plan-name {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--color-text);
      margin-bottom: var(--space-xs);
    }

    .plan-credits {
      font-size: 0.875rem;
      color: var(--color-text-muted);
      margin-bottom: var(--space-md);
    }

    .plan-price {
      font-size: 2rem;
      font-weight: 800;
      letter-spacing: -0.03em;
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 0.25rem;
    }

    .plan-period {
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      margin-bottom: var(--space-lg);
    }

    .plan-btn {
      margin-top: auto;
      padding: 0.75rem;
      border-radius: var(--radius);
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      border: none;
      text-align: center;
      transition: transform var(--duration-fast) var(--ease-out);
    }

    .plan-btn:hover {
      transform: translateY(-1px);
    }

    .plan-btn--primary {
      background: var(--gradient-primary);
      color: var(--palette-white);
      box-shadow: 0 4px 16px rgb(139 92 246 / 30%);
    }

    .plan-btn--secondary {
      background: var(--color-surface-hover);
      border: 1px solid var(--color-border);
      color: var(--color-text);
    }

    .plan-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .tax-note {
      margin-top: var(--space-md);
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      text-align: center;
    }

    .extras-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: var(--space-lg);
    }

    .extra-card {
      padding: var(--space-lg);
      border-radius: var(--radius-lg);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .extra-credits {
      font-size: 2rem;
      font-weight: 800;
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: var(--space-xs);
    }

    .extra-price {
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--color-text);
      margin-bottom: var(--space-xs);
    }

    .extra-note {
      font-size: 0.8125rem;
      color: var(--color-text-muted);
      margin-bottom: var(--space-lg);
    }

    .success-banner {
      padding: var(--space-md) var(--space-lg);
      border-radius: var(--radius);
      background: rgb(16 185 129 / 10%);
      border: 1px solid rgb(16 185 129 / 30%);
      color: var(--palette-emerald-400);
      font-size: 0.875rem;
      font-weight: 500;
      margin-bottom: var(--space-lg);
      text-align: center;
    }

    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-md);
      padding: var(--space-2xl) 0;
      color: var(--color-text-muted);
    }

    .error-state {
      padding: var(--space-lg);
      border-radius: var(--radius);
      background: rgb(244 63 94 / 8%);
      border: 1px solid rgb(251 113 133 / 25%);
      color: var(--color-error);
      text-align: center;
    }

    /* ── Confirmation modal ── */
    .modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 100;
      background: rgb(0 0 0 / 60%);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--space-lg);
      animation: fadeIn 120ms var(--ease-out) both;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    .modal {
      width: 100%;
      max-width: 440px;
      padding: var(--space-xl);
      border-radius: var(--radius-lg);
      background: var(--color-surface);
      border: 1px solid var(--color-border-strong);
      box-shadow: 0 24px 64px rgb(0 0 0 / 50%);
      animation: slideUp 180ms var(--ease-out) both;
    }

    @keyframes slideUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .modal-title {
      font-size: 1.125rem;
      font-weight: 700;
      color: var(--color-text);
      margin-bottom: var(--space-xs);
    }

    .modal-plan-row {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      margin: var(--space-md) 0;
      padding: var(--space-md);
      border-radius: var(--radius);
      background: var(--color-surface-hover);
      border: 1px solid var(--color-border);
    }

    .modal-plan-name {
      font-size: 1.25rem;
      font-weight: 800;
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .modal-plan-price {
      margin-left: auto;
      font-size: 1rem;
      font-weight: 700;
      color: var(--color-text);
    }

    .modal-plan-credits {
      font-size: 0.8125rem;
      color: var(--color-text-muted);
    }

    .modal-proration {
      padding: var(--space-sm) var(--space-md);
      border-radius: var(--radius);
      font-size: 0.8125rem;
      line-height: 1.5;
      margin-bottom: var(--space-lg);
    }

    .modal-proration--upgrade {
      background: rgb(139 92 246 / 8%);
      border: 1px solid rgb(139 92 246 / 25%);
      color: var(--color-text-muted);
    }

    .modal-proration--downgrade {
      background: rgb(16 185 129 / 8%);
      border: 1px solid rgb(16 185 129 / 25%);
      color: var(--color-text-muted);
    }

    .modal-actions {
      display: flex;
      gap: var(--space-sm);
    }

    .modal-btn {
      flex: 1;
      padding: 0.75rem;
      border-radius: var(--radius);
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      border: none;
      transition: transform var(--duration-fast) var(--ease-out), opacity var(--duration-fast);
    }

    .modal-btn:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    .modal-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .modal-btn--primary {
      background: var(--gradient-primary);
      color: var(--palette-white);
      box-shadow: 0 4px 16px rgb(139 92 246 / 30%);
    }

    .modal-btn--ghost {
      background: var(--color-surface-hover);
      border: 1px solid var(--color-border);
      color: var(--color-text);
    }

    @media (max-width: 639px) {
      .current-plan {
        flex-direction: column;
        align-items: stretch;
      }

      .current-plan-badge {
        align-self: flex-start;
      }

      .manage-btn {
        width: 100%;
        text-align: center;
      }
    }
    `,
  ];

  @state() private user: UserMe | null = null;
  @state() private billing: BillingInfo | null = null;
  @state() private loading = true;
  @state() private error = '';
  @state() private actionLoading = false;
  @state() private upgradeSuccess = false;
  @state() private confirmPlan: SubscriptionPlan | null = null;

  connectedCallback() {
    super.connectedCallback();
    void this.loadData();
  }

  private async loadData() {
    this.loading = true;
    this.error = '';

    try {
      const [user, billing] = await Promise.all([getMe(), getBillingInfo()]);
      this.user = user;
      this.billing = billing;
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) return;
      this.error = err instanceof ApiClientError ? err.message : 'Error al cargar datos';
    } finally {
      this.loading = false;
    }
  }

  private handleSubscribe(plan: SubscriptionPlan) {
    const sub = this.billing?.subscription;
    const hasActiveSub = sub?.status === 'active' || sub?.status === 'past_due';

    if (hasActiveSub) {
      // Show confirmation modal before committing the plan change
      this.confirmPlan = plan;
      return;
    }

    void this.executeSubscribe(plan);
  }

  private async executeSubscribe(plan: SubscriptionPlan) {
    this.actionLoading = true;
    this.upgradeSuccess = false;
    this.error = '';
    try {
      const result = await createSubscriptionCheckout(plan);
      if ('upgraded' in result && result.upgraded) {
        // Plan changed in-place — no redirect needed, just reload billing data
        this.upgradeSuccess = true;
        await this.loadData();
      } else if ('url' in result) {
        window.location.href = result.url;
      }
    } catch (err) {
      this.error = err instanceof ApiClientError ? err.message : 'Error al cambiar el plan';
    } finally {
      this.actionLoading = false;
      this.confirmPlan = null;
    }
  }

  private async handleConfirmUpgrade() {
    if (!this.confirmPlan) return;
    await this.executeSubscribe(this.confirmPlan);
  }

  private handleCancelModal() {
    this.confirmPlan = null;
  }

  private async handleBuyCredits() {
    this.actionLoading = true;
    try {
      const { url } = await createCreditsCheckout();
      window.location.href = url;
    } catch (err) {
      this.error = err instanceof ApiClientError ? err.message : 'Error al crear checkout';
      this.actionLoading = false;
    }
  }

  private async handleManageSubscription() {
    this.actionLoading = true;
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      this.error = err instanceof ApiClientError ? err.message : 'Error al abrir portal';
      this.actionLoading = false;
    }
  }

  private subscriptionBadge(sub: NonNullable<BillingInfo['subscription']>) {
    switch (sub.status) {
      case 'active':
        return html`<span class="current-plan-badge badge--active">Activo</span>`;
      case 'canceling':
        return html`<span class="current-plan-badge badge--canceling">Cancela al final del periodo</span>`;
      case 'past_due':
        return html`<span class="current-plan-badge badge--canceling">Pago pendiente</span>`;
      default:
        return html`<span class="current-plan-badge badge--canceled">Cancelado</span>`;
    }
  }

  private formatPeriodEnd(iso: string | null): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  private get showSuccess(): boolean {
    return new URLSearchParams(location.search).has('success');
  }

  private renderConfirmModal() {
    if (!this.confirmPlan) return '';

    const newPlan = PLANS.find((p) => p.key === this.confirmPlan);
    if (!newPlan) return '';

    const currentPlan = PLANS.find((p) => p.key === this.billing?.subscription?.plan);
    const isUpgrade = currentPlan ? newPlan.price > currentPlan.price : true;

    const prorationMsg = isUpgrade
      ? `Se cobrará ahora mismo la diferencia proporcional a la tarjeta guardada en tu cuenta. El cambio solo se aplica si el pago se completa correctamente.`
      : `Se generará un crédito proporcional que se aplicará en tu próxima factura. El plan se actualiza de inmediato.`;

    return html`
      <div class="modal-backdrop" @click=${(e: Event) => { if (e.target === e.currentTarget) this.handleCancelModal(); }}>
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <p class="modal-title" id="modal-title">Confirmar cambio de plan</p>

          <div class="modal-plan-row">
            <div>
              <p class="modal-plan-name">${newPlan.name}</p>
              <p class="modal-plan-credits">${newPlan.credits} créditos / mes</p>
            </div>
            <p class="modal-plan-price">${newPlan.price}€/mes</p>
          </div>

          <p class="modal-proration modal-proration--${isUpgrade ? 'upgrade' : 'downgrade'}">
            ${prorationMsg}
          </p>

          <div class="modal-actions">
            <button
              class="modal-btn modal-btn--ghost"
              @click=${() => this.handleCancelModal()}
              ?disabled=${this.actionLoading}
            >
              Cancelar
            </button>
            <button
              class="modal-btn modal-btn--primary"
              @click=${() => void this.handleConfirmUpgrade()}
              ?disabled=${this.actionLoading}
            >
              ${this.actionLoading ? 'Procesando…' : 'Confirmar cambio'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading-state animate-fade-up">
          <div class="spinner" role="status" aria-label="Cargando"></div>
          <p>Cargando créditos…</p>
        </div>
      `;
    }

    if (this.error) {
      return html`<div class="error-state">${this.error}</div>`;
    }

    if (!this.user) return html``;

    const sub = this.billing?.subscription;
    const currentPlan = sub?.plan;

    return html`
      ${this.renderConfirmModal()}
      <div class="animate-fade-up">
        ${this.showSuccess
          ? html`<div class="success-banner">Pago completado correctamente. Tus créditos se han actualizado.</div>`
          : ''}
        ${this.upgradeSuccess
          ? html`<div class="success-banner">Plan actualizado correctamente. Tus créditos se han ajustado al nuevo plan.</div>`
          : ''}

        <section class="hero">
          <div class="hero-content">
            <span class="hero-eyebrow">✦ Créditos</span>
            <h1>Gestiona tus créditos</h1>
            <p class="subtitle">Elige un plan mensual o compra créditos extra.</p>
          </div>
        </section>

        <!-- Current subscription status -->
        <div class="current-plan">
          <div class="current-plan-info">
            <p class="current-plan-label">Tu plan actual</p>
            <p class="current-plan-name">
              ${sub ? PLANS.find((p) => p.key === sub.plan)?.name ?? sub.plan : 'Sin plan'}
            </p>
            ${sub?.status === 'canceling' && sub.currentPeriodEnd
              ? html`<p class="current-plan-note">Activa hasta el ${this.formatPeriodEnd(sub.currentPeriodEnd)}</p>`
              : ''}
          </div>
          ${sub
            ? html`
              ${this.subscriptionBadge(sub)}
              <button
                class="manage-btn"
                @click=${() => void this.handleManageSubscription()}
                ?disabled=${this.actionLoading}
              >
                Gestionar suscripción
              </button>
            `
            : html`<span class="current-plan-badge badge--none">Sin suscripción</span>`}
        </div>

        <!-- Plans section -->
        <section class="section">
          <div class="section-header">
            <h2>Planes de suscripción</h2>
          </div>

          <div class="plans-grid">
            ${PLANS.map((plan) => {
              const isCurrent = currentPlan === plan.key;
              const hasActiveSub = sub?.status === 'active' || sub?.status === 'past_due';
              const btnLabel = isCurrent
                ? 'Plan actual'
                : hasActiveSub
                  ? 'Cambiar plan'
                  : 'Suscribirme';
              return html`
                <div class="plan-card ${isCurrent ? 'plan-card--current' : ''}">
                  ${isCurrent ? html`<span class="plan-current-tag">Plan actual</span>` : ''}
                  <p class="plan-name">${plan.name}</p>
                  <p class="plan-credits">${plan.credits} créditos / mes</p>
                  <p class="plan-price">${plan.price}€</p>
                  <p class="plan-period">por mes</p>
                  <button
                    class="plan-btn ${isCurrent ? 'plan-btn--secondary' : 'plan-btn--primary'}"
                    @click=${() => void this.handleSubscribe(plan.key)}
                    ?disabled=${isCurrent || this.actionLoading}
                  >
                    ${btnLabel}
                  </button>
                </div>
              `;
            })}
          </div>

          <p class="tax-note">Precio sin IVA — se aplica según tu localización.</p>
        </section>

        <!-- Extra credits section -->
        <section class="section">
          <div class="section-header">
            <h2>Créditos extra</h2>
          </div>

          <div class="extras-grid">
            <div class="extra-card">
              <p class="extra-credits">+20</p>
              <p class="extra-price">15€</p>
              <p class="extra-note">Los créditos extra se consumen antes que los de tu suscripción.</p>
              <button
                class="plan-btn plan-btn--primary"
                @click=${() => void this.handleBuyCredits()}
                ?disabled=${this.actionLoading}
              >
                Comprar
              </button>
            </div>
          </div>

          <p class="tax-note">Precio sin IVA — se aplica según tu localización.</p>
        </section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'credits-page': CreditsPage;
  }
}
